import React, { useState, useEffect } from "react";
import {Popover, Button, Spin, Select, Row, Space, Badge, Typography, Layout, message} from "antd";

import dayjs from "dayjs";
import { fetchWrapper } from "../../helpers";
import { useNavigate } from "react-router-dom";
import { useOrganization } from "../../context/organizationContext";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {ImeanHeader} from "../../components/Header/Header";
import {StickyNavigationHeader} from "../../components/Navigation/StickyNavigationHeader";

import {
    DndContext,
    useSensor,
    useSensors,
    TouchSensor,
    MouseSensor,
    KeyboardSensor,
    closestCenter
} from "@dnd-kit/core";
import {useDraggable} from "@dnd-kit/core";

import DroppableDay from "../../components/Column/DroppableDay"
import NoteModal from "../../components/modals/NoteModal/NoteModal";
import NoteChip from "../../components/Chips/NoteChip";
import threadsIcon from "../icons/threads-icon.svg";

const baseUrl = `${process.env.REACT_APP_API_URL}`;

const SOCIAL_ICON_LINKS = {
    linkedin: "https://cdn.builder.io/api/v1/image/assets/TEMP/d25408c5dff59b86cb5e75674b2a798a87cd455bde0adf0ceb8c9a670d1405e8?apiKey=300ff06bbf3a4f5c9c73ca7577720bf1&",
    facebook: "https://cdn.builder.io/api/v1/image/assets/TEMP/f8432315b7e3752ab28ffd9571f771342eef6b676c1f20e4b8a5e6ee48c70349?apiKey=300ff06bbf3a4f5c9c73ca7577720bf1&",
    instagram: "https://cdn.builder.io/api/v1/image/assets/TEMP/497038a4df4615b7ef35db19c49364c0fdcd7e3749f9681fe807b55de2426e88?apiKey=300ff06bbf3a4f5c9c73ca7577720bf1&",
    threads: threadsIcon,
};

const CalendarPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const org = useOrganization();
    const user = useSelector((obj) => obj.auth.user);

    const getInitialDateRange = () => {
        try {
            const savedRange = localStorage.getItem("calendarDatesRange");
            if (savedRange) {
                const parsedRange = JSON.parse(savedRange);
                return {
                    pastDays: parsedRange.pastDays || 1,
                    futureDays: parsedRange.futureDays || 1,
                };
            }
        } catch (error) {
            console.error("Error loading date range from localStorage:", error);
        }
        return { pastDays: 1, futureDays: 1 };
    };

    const initialRange = getInitialDateRange();
    const [pastDays, setPastDays] = useState(initialRange.pastDays);
    const [futureDays, setFutureDays] = useState(initialRange.futureDays);
    const [calendarDays, setCalendarDays] = useState([]);
    const [posts, setPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [orgUsers, setOrgUsers] = useState([]);
    const [noteModalOpen, setNoteModalOpen] = useState(false);
    const [selectedDayForAction, setSelectedDayForAction] = useState(null);
    const [popoverOpenDayId, setPopoverOpenDayId] = useState(null);
    const [notes, setNotes] = useState([]);
    const [noteModalMode, setNoteModalMode] = useState("create"); // "create" | "edit"
    const [editingNote, setEditingNote] = useState(null);
    const calendarScrollRef = React.useRef(null);
    const [activeDrag, setActiveDrag] = useState(null); // {type: 'post'|'note', data: {...}}

    function isInteractive(el) {
        if (!(el instanceof Element)) return false;
        const tags = ['BUTTON', 'A', 'INPUT', 'TEXTAREA', 'SELECT', 'LABEL'];
        if (tags.includes(el.tagName)) return true;
        if (el.closest('button, a, input, textarea, select, label')) return true;
        if (el.closest('[data-no-dnd="true"]')) return true;
        return false;
    }

    // Fetch organization users
    useEffect(() => {
        if (org?.organization?.employees && org?.organization?.moderatorExist && user?.moderatorOrg) {
            setOrgUsers(org.organization.employees);
        }
    }, [org, user]);

    class SafeMouseSensor extends MouseSensor {
        static activators = [{
            eventName: 'onMouseDown',
            handler: ({nativeEvent}) => !isInteractive(nativeEvent.target)
        }];
    }

    class SafeTouchSensor extends TouchSensor {
        static activators = [{
            eventName: 'onTouchStart',
            handler: ({nativeEvent}) => !isInteractive(nativeEvent.target)
        }];
    }

    const sensors = useSensors(
        useSensor(SafeTouchSensor, {activationConstraint: {delay: 200, tolerance: 5}}),
        useSensor(SafeMouseSensor, {activationConstraint: {distance: 8}}),
        useSensor(KeyboardSensor)
    );

    useEffect(() => {
        try {
            localStorage.setItem("calendarDatesRange", JSON.stringify({ pastDays, futureDays }));
        } catch (error) {
            console.error("Error saving date range to localStorage:", error);
        }
    }, [pastDays, futureDays]);

    useEffect(() => {
        const today = dayjs();
        const startDate = today.subtract(pastDays, "day").startOf("day");
        const endDate = today.add(futureDays, "day").endOf("day");
        setIsLoading(true);


        Promise.all([
            fetchWrapper.get(`${baseUrl}/post/calendar?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`),
            fetchWrapper.get(`${baseUrl}/notes/calendar?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`),
        ])
            .then(([postsData, notesData]) => {
                setPosts(postsData || []);
                setNotes(notesData || []);
            })
            .catch((error) => {
                console.error("Error fetching calendar data:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [pastDays, futureDays]);

    useEffect(() => {
        const today = dayjs();
        const startDate = today.subtract(pastDays, "day");
        const endDate = today.add(futureDays, "day");
        const days = [];
        let currentDate = startDate;

        const latestPostsByContentPlanId = {};

        posts.forEach(post => {
            const dateKey = dayjs(post.date).format("YYYY-MM-DD");
            const key = `${post.contentPlanId}_${dateKey}`;

            if (!latestPostsByContentPlanId[key] || post.id > latestPostsByContentPlanId[key].id) {
                latestPostsByContentPlanId[key] = post;
            }
        });

        while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, "day")) {
            const currentDateFormatted = currentDate.format("YYYY-MM-DD");

            let postsForDay = Object.values(latestPostsByContentPlanId).filter(post => {
                const postDate = dayjs(post.date);
                return postDate.format("YYYY-MM-DD") === currentDateFormatted;
            });

            // Apply user filter only to organization posts
            if (selectedUserId && selectedUserId !== '') {
                const selectedUser = orgUsers.find(el => String(el.id) === String(selectedUserId));
                postsForDay = postsForDay.filter(post => {
                    if (!post.isOrg) return true;
                    const userIdEq = String(post.UserId) === String(selectedUserId);
                    const nameEq = selectedUser
                        ? post.username === (selectedUser.fullName || selectedUser.email)
                        : false;
                    return userIdEq || nameEq;
                });
            }
            const notesForDay = (notes || []).filter(n =>
                (n.day || dayjs(n.date).format("YYYY-MM-DD")) === currentDateFormatted
            ).sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());

            days.push({
                date: currentDate,
                posts: postsForDay,
                notes: notesForDay
            });
            currentDate = currentDate.add(1, "day");
        }

        setCalendarDays(days);
    }, [pastDays, futureDays, posts, selectedUserId, notes]);

    // ------------------------
    // Handlers for note modal
    // ------------------------

    const openNoteModalForDay = (day) => {
        setSelectedDayForAction(day);
        setNoteModalOpen(true);
    };


    const handleNoteCreated = (createdNote) => {
        const dayStr =
            selectedDayForAction
                ? dayjs(selectedDayForAction).format("YYYY-MM-DD")
                : dayjs(createdNote?.date).format("YYYY-MM-DD");

        const normalized = {
            id: createdNote.id,
            title: createdNote.title,
            description: createdNote.description,
            date: createdNote.date,
            day: dayStr,
            kind: 'note'
        }

        setNotes((prev) =>
            [...(prev || []), normalized].sort(
                (a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf()
            )
        )
    };

    //----------------------
    // Callbacks
    //----------------------
    const onEditNote = (note) => {
        setEditingNote(note);
        setNoteModalMode("edit");
        setNoteModalOpen(true);
    };

    const onDeleteNote = async (note) => {
        try {
            await fetchWrapper.delete(`${baseUrl}/notes/remove/${note.id}`);
            setNotes(prev => (prev || []).filter(n => n.id !== note.id));
            message.success(t("publicationCalendar.noteModal.messages.delete"))
        } catch (e) {
            message.error(t("publicationCalendar.noteModal.errors.deleteNote"))
        }
    };

    // ------------------------
    // Handlers for dragDrop
    // ------------------------

    const onDragStart = (evt) => {
        const data = evt.active?.data?.current;
        if (!data) return;
        console.log(data.payload.isPublished);
        if (data.type === 'post' && data.payload?.isPublished) {
            message.info(t("publicationCalendar.messages.cantDragPublished"));
        }
        setActiveDrag(data);
    };

    const onDragEnd = async (evt) => {
        const active = evt.active?.data?.current;
        const over = evt.over?.data?.current;
        setActiveDrag(null);
        if (!active || !over || over.type !== 'day') return;

        const targetDay = over.dayObj;

        if (active.type === 'post') {
            const { id, date, isPublished } = active.payload;
            if (isPublished) return;
            const newDayStr = targetDay.format("YYYY-MM-DD");
            const old = dayjs(date);
            const newDate = dayjs(`${newDayStr} ${old.format("HH:mm")}`);
            if (newDate.isBefore(dayjs(), "minute")) {
                message.warning(t("publicationCalendar.messages.cantDragToPast"));
                return;
            }
            if (old.format("YYYY-MM-DD") === newDayStr) return;
            try {
                await fetchWrapper.put(`${baseUrl}/post/update/${id}`, {date: newDate.toISOString()});
                setPosts(prev => (prev || []).map(p => p.id === id ? {...p, date: newDate.toISOString()} : p));
            } catch {
                message.error(t("publicationCalendar.messages.failedDragPost"));
            }
        }

        if (active.type === 'note') {
            const {id, date} = active.payload;
            const newDayStr = targetDay.format("YYYY-MM-DD");
            const oldDayStr = dayjs(date).format("YYYY-MM-DD");
            if (oldDayStr === newDayStr) return;
            try {
                const updated = await fetchWrapper.put(`${baseUrl}/notes/update/${id}`, {date: newDayStr});
                setNotes(prev =>
                    (prev || [])
                        .map(n => n.id === id ? { ...n, ...updated, day: newDayStr } : n)
                        .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
                );
            } catch {
                message.error(t("publicationCalendar.messages.failedDragNote"));
            }
        }
    };

    // ------------------------
    // Helpers
    // ------------------------

    const handleSetPastDays = (days) => {
        setPastDays(days);
    };

    const handleSetFutureDays = (days) => {
        setFutureDays(days);
    };

    const getPostBgColor = (contentType, isOrg) => {
        if (contentType === "Post" && !isOrg) {
            return "bg-purple-100";
        } else if (contentType === "Article" && !isOrg) {
            return "bg-blue-100";
        } else if (contentType === "Post" && isOrg) {
            return "bg-orange-100";
        } else if (contentType === "Article" && isOrg) {
            return "bg-yellow-100";
        } else {
            return "bg-blue-200";
        }
    };

    const getTruePostStatus = (status, error) => {
        const errMap = Object.keys(error || {});
        if (status === "error" || errMap.length) {
            return "error";
        } else {
            return status;
        }
    };

    const getBorderColor = (contentType, isOrg, status) => {
        if (status === "error") {
            return "border-red-500";
        }
        if (status === "published") {
            return "border-green-500";
        }

        if (contentType === "Post" && !isOrg) {
            return "border-purple-500";
        } else if (contentType === "Article" && !isOrg) {
            return "border-blue-500";
        } else if (contentType === "Post" && isOrg) {
            return "border-orange-500";
        } else if (contentType === "Article" && isOrg) {
            return "border-yellow-500";
        } else {
            return "border-gray-500";
        }
    };

    const getPostTextColor = (contentType, isOrg) => {
        if (contentType === "Post" && !isOrg) {
            return "text-purple-700";
        } else if (contentType === "Article" && !isOrg) {
            return "text-blue-700";
        } else if (contentType === "Post" && isOrg) {
            return "text-orange-700";
        } else if (contentType === "Article" && isOrg) {
            return "text-yellow-700";
        } else {
            return "text-gray-700";
        }
    };

    const isNavButtonActive = (days, direction) => {
        return (direction === "past" && days === pastDays) || (direction === "future" && days === futureDays);
    };

    const getNavButtonClass = (days, direction) => {
        const baseClass = "px-2 py-1 text-sm font-medium rounded mx-1 transition-colors duration-200";
        return isNavButtonActive(days, direction) ? `${baseClass} bg-violet-600 text-white` : `${baseClass} bg-gray-200 hover:bg-gray-300 text-gray-700`;
    };

    function DraggablePost({ post, children }) {
        const status = typeof post.status === 'string' ? post.status.toLowerCase() : '';
        const isPublished = status === 'published';

        const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
            id: `post-${post.id}`,
            data: { type: 'post', payload: { id: post.id, date: post.date, isPublished } },
            disabled: isPublished,
        });

        const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;

        return (
            <div
                ref={setNodeRef}
                style={style}
                {...attributes}
                {...listeners}
                className={`relative ${isDragging ? 'opacity-60' : ''} ${isPublished ? 'cursor-not-allowed opacity-70' : ''}`}
                // onMouseDown={() => { if (isPublished) message.info(t("publicationCalendar.messages.cantDragPublished")); }}
                // onTouchStart={() => { if (isPublished) message.info(t("publicationCalendar.messages.cantDragPublished")); }}
            >
                {children}
            </div>
        );
    }

    function DraggableNote({note, children}) {
        const {attributes, listeners, setNodeRef, transform, isDragging} = useDraggable({
            id: `note-${note.id}`,
            data: {type: 'note', payload: {id: note.id, date: note.date}}
        });
        const style = transform ? {transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`} : undefined;

        return (
            <div
                ref={setNodeRef}
                style={style}
                {...attributes}
                {...listeners}
                className={`relative ${isDragging ? 'opacity-60' : ''}`}
            >
                {children}
            </div>
        );
    }

    const getPostContent = (post) => {
        return (
            <div className="max-w-[300px] sm:max-w-[400px]">
                <h3 className="max-w-sm text-violet-800 text-sm md:text-md font-semibold">
                    <strong>{post.title}</strong>
                </h3>

                {/*{getTruePostStatus(post.status, post.error)  === 'error' && post.error && (*/}

                <div className="mt-1 flex flex-col gap-1">
                    {post?.sn?.map((sn) => (
                        <div key={sn}
                             className={`${post.error?.[sn.toLowerCase()] ? "bg-red-50 border-red-100" : post.status === "pending" ? "bg-gray-50 border-gray-100" : "bg-green-50 border-green-100"} p-1 border flex gap-1.5 rounded text-xs overflow-hidden`}>
                            <img className={`w-4 h-4 ${sn.toLowerCase() === 'threads' ? 'rounded-full' : ''}`} key={sn} src={SOCIAL_ICON_LINKS[sn.toLowerCase()]} alt={sn}/>
                            {post.error?.[sn.toLowerCase()] ? (
                                <div>
                                    <p className="font-semibold text-xs text-red-800">{t("publicationCalendar.postStatuses.error")}</p>
                                    <p className="text-xs text-red-800 max-w-[300px] text-wrap">{post.error?.[sn.toLowerCase()]}</p>
                                </div>
                            ) : post.status === "pending" ? (
                                <p>{t("publicationCalendar.postStatuses.pending")}</p>
                            ) : (
                                <p className="font-medium text-xs text-green-800">{t("publicationCalendar.postStatuses.published")}</p>
                            )}
                        </div>
                    ))}
                </div>

                <div className="my-4 flex justify-between gap-2 items-center">
                    <p className="text-sm font-medium text-black/50">{post.username}</p>
                    <p className="text-xs text-right text-black/25">{dayjs(post.date).format("YYYY-MM-DD HH:mm")}</p>
                </div>

                <div className="mt-4">
                    <Button type="primary" className="w-full bg-violet-600 hover:!bg-violet-800 "
                            onClick={() => navigate(`/create-post/${post.contentPlanId}`)}>
                        {t("publicationCalendar.goToPost")}
                    </Button>
                </div>
            </div>
        );
    };

    const getPostTypeLabel = (contentType, isOrg) => {
        if (contentType === "Post" && !isOrg) {
            return `${t("publicationCalendar.postTypes.quickPersonal")} ${!!(org?.organization?.moderatorExist && user?.moderatorOrg) ? t("publicationCalendar.postTypes.personal") : ""}`;
        } else if (contentType === "Article" && !isOrg) {
            return `${t("publicationCalendar.postTypes.planPersonal")} ${!!(org?.organization?.moderatorExist && user?.moderatorOrg) ? t("publicationCalendar.postTypes.personal") : ""}`;
        } else if (contentType === "Post" && isOrg) {
            return t("publicationCalendar.postTypes.quickEmployee");
        } else if (contentType === "Article" && isOrg) {
            return t("publicationCalendar.postTypes.planEmployee");
        } else {
            return t("publicationCalendar.postTypes.other");
        }
    };

    const today = dayjs();

    return (
        <>
            {/* Header */}
            <div className="w-full">
                <ImeanHeader initialDark={true}/>
                <StickyNavigationHeader headerTitle={t('home.postsCalendar')}
                                        onBackClick={() => navigate('/home')}></StickyNavigationHeader>
            </div>



            {/* Toolbar — responsive & compact */}
            <div className="px-2 sm:px-4 md:px-6 mb-3">
                <div className="bg-white shadow-sm border border-gray-100 rounded-2xl overflow-hidden">
                    {/* Mobile: Compact vertical layout */}
                    <div className="md:hidden">
                        {/* Top row: Today button and date */}
                        <div className="p-2.5 flex items-center justify-between border-b border-gray-100">
                            <Button
                                className="!rounded-lg !px-4 !py-1.5 !text-xs !font-semibold !h-auto"
                                type="primary"
                                onClick={() => {
                                    handleSetPastDays(0);
                                    handleSetFutureDays(0);
                                }}
                            >
                                Today
                            </Button>

                            <div className="text-xs font-semibold text-gray-700">
                                {dayjs().subtract(pastDays, "day").format("MMM D")} – {dayjs().add(futureDays, "day").format("MMM D")}
                            </div>
                        </div>

                        {/* Past/Future buttons */}
                        <div className="p-2.5 border-b border-gray-100 space-y-2">
                            <div className="flex items-center gap-1">
                                <span className="text-[10px] font-semibold text-gray-500 uppercase w-12 shrink-0">Past:</span>
                                <div className="flex gap-1 flex-1">
                                    {[3,7,30,60].map((n) => (
                                        <button
                                            key={`p-${n}`}
                                            className={`flex-1 px-2 py-1 text-xs rounded-lg font-medium transition-all ${
                                                isNavButtonActive(n, "past") 
                                                    ? "bg-violet-600 text-white shadow-sm" 
                                                    : "bg-gray-50 text-gray-600"
                                            }`}
                                            onClick={() => handleSetPastDays(n)}
                                        >
                                            {n}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="flex items-center gap-1">
                                <span className="text-[10px] font-semibold text-gray-500 uppercase w-12 shrink-0">Future:</span>
                                <div className="flex gap-1 flex-1">
                                    {[3,7,30,60].map((n) => (
                                        <button
                                            key={`f-${n}`}
                                            className={`flex-1 px-2 py-1 text-xs rounded-lg font-medium transition-all ${
                                                isNavButtonActive(n, "future") 
                                                    ? "bg-violet-600 text-white shadow-sm" 
                                                    : "bg-gray-50 text-gray-600"
                                            }`}
                                            onClick={() => handleSetFutureDays(n)}
                                        >
                                            {n}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* View selector */}
                        <div className="p-2.5">
                            <Select
                                size="small"
                                className="w-full"
                                value={
                                    pastDays === 0 && futureDays === 0
                                        ? "day"
                                        : pastDays === 3 && futureDays === 3
                                            ? "week"
                                            : pastDays === 30 && futureDays === 30
                                                ? "month"
                                                : "custom"
                                }
                                onChange={(v) => {
                                    if (v === "day") {
                                        handleSetPastDays(0); handleSetFutureDays(0);
                                    } else if (v === "week") {
                                        handleSetPastDays(3); handleSetFutureDays(3);
                                    } else if (v === "month") {
                                        handleSetPastDays(30); handleSetFutureDays(30);
                                    }
                                }}
                                options={[
                                    { value: "day", label: t("publicationCalendar.toolbar.viewOptions.day") },
                                    { value: "week", label: t("publicationCalendar.toolbar.viewOptions.week") },
                                    { value: "month", label: t("publicationCalendar.toolbar.viewOptions.month") },
                                    { value: "custom", label: t("publicationCalendar.toolbar.viewOptions.custom") },
                                ]}
                            />
                        </div>

                        {/* User filter */}
                        {org?.organization?.moderatorExist && user?.moderatorOrg && (
                            <div className="p-2.5 border-t border-gray-100">
                                <label className="block text-[10px] font-semibold text-gray-600 mb-1.5 uppercase">
                                    {t("publicationCalendar.filters.byUser")}
                                </label>
                                <Select
                                    placeholder={t("publicationCalendar.filters.allUsers")}
                                    value={selectedUserId}
                                    onChange={(value) => setSelectedUserId(value)}
                                    size="small"
                                    className="w-full"
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={[
                                        { value: "", label: t("publicationCalendar.filters.allUsers") },
                                        ...orgUsers.map((u) => ({
                                            value: u.id,
                                            label: u.fullName || u.email,
                                        })),
                                    ]}
                                />
                            </div>
                        )}
                    </div>

                    {/* Desktop: Original layout */}
                    <div className="hidden md:block p-4">
                        <div className="grid grid-cols-[auto,1fr,auto,auto] items-center gap-4">
                            <div className="flex items-center gap-2">
                                <Button
                                    className="!rounded-lg !font-medium"
                                    size="middle"
                                    type="primary"
                                    onClick={() => {
                                        handleSetPastDays(0);
                                        handleSetFutureDays(0);
                                    }}
                                >
                                    {t("publicationCalendar.toolbar.today")}
                                </Button>

                                <div className="flex items-center gap-1">
                                    <span className="text-xs text-gray-500 mr-1">{t("publicationCalendar.toolbar.past")}</span>
                                    {[3,7,30,60].map((n) => (
                                        <Button
                                            key={`P-${n}`}
                                            className={`!px-2 !py-1 !text-xs !rounded-md ${
                                                isNavButtonActive(n, "past") ? "!bg-violet-600 !text-white" : ""
                                            }`}
                                            onClick={() => handleSetPastDays(n)}
                                        >
                                            {n}
                                        </Button>
                                    ))}
                                    <span className="text-xs text-gray-500 mx-2">•</span>
                                    <span className="text-xs text-gray-500 mr-1">{t("publicationCalendar.toolbar.future")}</span>
                                    {[3,7,30,60].map((n) => (
                                        <Button
                                            key={`F-${n}`}
                                            className={`!px-2 !py-1 !text-xs !rounded-md ${
                                                isNavButtonActive(n, "future") ? "!bg-violet-600 !text-white" : ""
                                            }`}
                                            onClick={() => handleSetFutureDays(n)}
                                        >
                                            {n}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            <div className="text-center">
                                <div className="text-sm md:text-base font-medium text-gray-700 leading-tight">
                                    {dayjs().subtract(pastDays, "day").format("MMM D")} –{" "}
                                    {dayjs().add(futureDays, "day").format("MMM D, YYYY")}
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-500">
                                    {t("publicationCalendar.toolbar.view")}
                                </span>
                                <Select
                                    size="middle"
                                    className="w-[180px]"
                                    value={
                                        pastDays === 0 && futureDays === 0
                                            ? t("publicationCalendar.toolbar.viewOptions.day")
                                            : pastDays === 3 && futureDays === 3
                                                ? t("publicationCalendar.toolbar.viewOptions.week")
                                                : pastDays === 30 && futureDays === 30
                                                    ? t("publicationCalendar.toolbar.viewOptions.month")
                                                    : pastDays >= 180 && futureDays >= 180
                                                        ? t("publicationCalendar.toolbar.viewOptions.year")
                                                        : t("publicationCalendar.toolbar.viewOptions.custom")
                                    }
                                    onChange={(v) => {
                                        if (v === "day") {
                                            handleSetPastDays(0); handleSetFutureDays(0);
                                        } else if (v === "week") {
                                            handleSetPastDays(3); handleSetFutureDays(3);
                                        } else if (v === "month") {
                                            handleSetPastDays(30); handleSetFutureDays(30);
                                        } else if (v === "year") {
                                            handleSetPastDays(180); handleSetFutureDays(180);
                                        }
                                    }}
                                    options={[
                                        { value: "day", label: t("publicationCalendar.toolbar.viewOptions.day") },
                                        { value: "week", label: t("publicationCalendar.toolbar.viewOptions.week") },
                                        { value: "month", label: t("publicationCalendar.toolbar.viewOptions.month") },
                                        { value: "year", label: t("publicationCalendar.toolbar.viewOptions.year") },
                                        { value: "custom", label: t("publicationCalendar.toolbar.viewOptions.custom") },
                                    ]}
                                />
                            </div>

                            {org?.organization?.moderatorExist && user?.moderatorOrg && (
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium">
                                        {t("publicationCalendar.filters.byUser")}
                                    </span>
                                    <Select
                                        placeholder={t("publicationCalendar.filters.allUsers")}
                                        value={selectedUserId}
                                        onChange={(value) => setSelectedUserId(value)}
                                        className="w-[240px]"
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={[
                                            { value: "", label: t("publicationCalendar.filters.allUsers") },
                                            ...orgUsers.map((u) => ({
                                                value: u.id,
                                                label: u.fullName || u.email,
                                            })),
                                        ]}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Calendar body */}
            <div className="calendar-page sm:p-2 md:p-4 overflow-auto">
                {/* Loading */}
                <div ref={calendarScrollRef}>
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center gap-5 h-40">
                            <Spin size="large"/>
                            <p className="text-gray-500">Loading posts…</p>
                        </div>
                    ) : (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragStart={onDragStart}
                            onDragEnd={onDragEnd}
                        >
                            <div className="calendar-grid grid grid-flow-col auto-cols-[minmax(220px,1fr)]">
                                {calendarDays.map((day, index) => (
                                    <DroppableDay
                                        key={index}
                                        day={day.date}
                                        isToday={day.date.isSame(today, "day")}
                                        t={t}
                                        isActionsOpen={popoverOpenDayId === day.date.format("YYYY-MM-DD")}
                                        onActionsOpenChange={(open) => setPopoverOpenDayId(open ? day.date.format("YYYY-MM-DD") : null)}
                                        onCreatePost={(d) => {
                                            if (d.isBefore(dayjs(), "day")) {
                                                message.warning(t("publicationCalendar.messages.cantCreatePostInPast"));
                                                return;
                                            }
                                            navigate(`/generate-post?date=${d.format("YYYY-MM-DD")}`);
                                        }}
                                        onCreateNote={(d) => openNoteModalForDay(d)}
                                    >
                                        {(isOver) => (
                                            <>
                                                <div
                                                    className={`day-posts p-3 space-y-3 min-h-[560px] ${isOver ? "ring-2 ring-amber-300 ring-offset-1" : ""}`}>
                                                    {(!day.posts?.length && !day.notes?.length) && (
                                                        <div className="text-center text-gray-300 text-sm pt-6">
                                                            {t("publicationCalendar.noPosts")}
                                                        </div>
                                                    )}

                                                    {/* Posts */}
                                                    {day.posts?.length > 0 && (
                                                        <>
                                                            {day.posts.map((post) => (
                                                                <DraggablePost key={`post_${post.id}`} post={post}>
                                                                    <Popover
                                                                        content={getPostContent(post)}
                                                                        title={getPostTypeLabel(post.contentType, post.isOrg)}
                                                                        trigger={['hover', 'focus']}
                                                                        mouseEnterDelay={0.06}
                                                                        mouseLeaveDelay={0.06}
                                                                        placement="bottom"
                                                                    >
                                                                        <div
                                                                            className={`relative cursor-pointer post-item p-2 rounded-lg border-l-4 shadow-sm transition-transform hover:scale-[1.01] ${getPostBgColor(
                                                                                post.contentType,
                                                                                post.isOrg
                                                                            )} ${getBorderColor(
                                                                                post.contentType,
                                                                                post.isOrg,
                                                                                getTruePostStatus(post.status, post.error)
                                                                            )}`}
                                                                        >
                                                                            <div className="flex justify-between gap-2">
                                                                                <div
                                                                                    className="post-time text-xs text-gray-500">
                                                                                    {dayjs(post.date).format("HH:mm")}
                                                                                </div>
                                                                                <div
                                                                                    className="flex items-center gap-1">
                                                                                    {post?.sn?.map((sn) => (
                                                                                        <span key={sn}
                                                                                              className={`${post.error?.[sn.toLowerCase()] ? "bg-red-500 shadow-red-500" : post.status === "pending" ? "bg-gray-300 shadow-gray-300" : "bg-green-500 shadow-green-500"} w-5 h-5 aspect-square shadow flex items-center justify-center rounded-full`}>
                                   <img src={SOCIAL_ICON_LINKS[sn.toLowerCase()]} alt={sn}
                                        className={`w-4 h-4 aspect-square ${sn.toLowerCase() === 'threads' ? 'rounded-full' : ''}`}/>
                                </span>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                            <div
                                                                                className={`mt-1 post-title text-sm font-medium ${getPostTextColor(post.contentType, post.isOrg)}`}>
                                                                                {post.title}
                                                                            </div>
                                                                        </div>
                                                                    </Popover>
                                                                </DraggablePost>
                                                            ))}
                                                        </>
                                                    )}

                                                    {/* Notes */}
                                                    {day.notes?.length > 0 && (
                                                        <div className="mt-3 space-y-2">
                                                            {day.notes.map((note) => (
                                                                <DraggableNote key={`note_${note.id}`} note={note}>
                                                                    <NoteChip
                                                                        note={note}
                                                                        onEdit={onEditNote}
                                                                        onDelete={onDeleteNote}
                                                                    />
                                                                </DraggableNote>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </DroppableDay>
                                ))}
                            </div>
                        </DndContext>
                    )}
                </div>

                {/* Legend */}
                <div className="calendar-legend mt-4 flex flex-wrap gap-3 justify-center text-xs">
                    <div className="flex items-center">
                        <span className="inline-block w-3 h-3 rounded-full bg-purple-500 mr-1"/>
                        <span>{t("publicationCalendar.legendLabels.quickPersonal")}</span>
                    </div>
                    <div className="flex items-center">
                        <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-1"/>
                        <span>{t("publicationCalendar.legendLabels.plannedPersonal")}</span>
                    </div>
                    {org?.organization?.moderatorExist && user?.moderatorOrg && (
                        <>
                            <div className="flex items-center">
                                <span className="inline-block w-3 h-3 rounded-full bg-orange-500 mr-1"/>
                                <span>{t("publicationCalendar.legendLabels.quickEmployee")}</span>
                            </div>
                            <div className="flex items-center">
                                <span className="inline-block w-3 h-3 rounded-full bg-yellow-500 mr-1"/>
                                <span>{t("publicationCalendar.legendLabels.plannedEmployee")}</span>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <NoteModal
                open={noteModalOpen}
                date={selectedDayForAction || dayjs()}
                onClose={() => {
                    setNoteModalOpen(false);
                    setNoteModalMode("create");
                    setEditingNote(null);
                }}
                onSuccess={(payload) => {
                    if (noteModalMode === "edit" && payload?.id) {
                        setNotes(prev =>
                            (prev || []).map(n => n.id === payload.id ? {...n, ...payload} : n)
                        );
                    } else if (payload?.id) {
                        handleNoteCreated(payload);
                    }
                }}
                onDeleted={(note) => {
                    setNotes(prev => (prev || []).filter(n => n.id !== note.id));
                }}
                fetchWrapper={fetchWrapper}
                baseUrl={baseUrl}
                mode={noteModalMode}
                initialNote={editingNote}
            />
        </>
    );

};

export default CalendarPage;
